import { useState, type ChangeEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from './Icon.tsx';
import { paymentsApi, type PaymentDoc, type PaymentDocKind } from '../features/payments/api.ts';
import { downloadPdf } from '../features/documents/api.ts';
import { fileToBase64, UPLOAD_ACCEPT } from '../lib/upload.ts';
import { t } from '../lib/strings.ts';

/**
 * Dialog shown before marking an award paid. Collects the required Payment
 * Voucher (single) + Payment Slip (single) and optional Other docs (multiple),
 * lets finance preview the merged package, then confirms mark-as-paid.
 */
export function MarkPaidDialog({
  requestId,
  refNumber,
  open,
  onOpenChange,
}: {
  requestId: string;
  refNumber: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const { data: docs } = useQuery({
    queryKey: ['paymentDocs', requestId],
    queryFn: () => paymentsApi.listDocs(requestId),
    enabled: open,
  });
  const docList = docs ?? [];
  const pv = docList.find((d) => d.kind === 'PV') ?? null;
  const slip = docList.find((d) => d.kind === 'SLIP') ?? null;
  const others = docList.filter((d) => d.kind === 'OTHER');

  const uploadMutation = useMutation({
    mutationFn: async ({ kind, file }: { kind: PaymentDocKind; file: File }) => {
      const { dataBase64, mime } = await fileToBase64(file);
      return paymentsApi.uploadDoc(requestId, { kind, fileName: file.name, mime, dataBase64 });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['paymentDocs', requestId] }),
    onError: () => setErr(t.payments.docInvalid),
  });

  const deleteMutation = useMutation({
    mutationFn: (docId: string) => paymentsApi.deleteDoc(requestId, docId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['paymentDocs', requestId] }),
  });

  const paidMutation = useMutation({
    mutationFn: () => paymentsApi.setPaid(requestId, true),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payments'] });
      onOpenChange(false);
    },
    onError: () => setErr(t.payments.updateFailed),
  });

  function pickSingle(kind: 'PV' | 'SLIP') {
    return (e: ChangeEvent<HTMLInputElement>) => {
      setErr(null);
      const file = e.target.files?.[0];
      e.target.value = '';
      if (file) uploadMutation.mutate({ kind, file });
    };
  }

  async function pickOthers(e: ChangeEvent<HTMLInputElement>) {
    setErr(null);
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    for (const file of files) {
      await uploadMutation.mutateAsync({ kind: 'OTHER', file }).catch(() => undefined);
    }
  }

  async function handlePreview() {
    setPreviewing(true);
    try {
      await downloadPdf(paymentsApi.packageUrl(requestId), `liyekunthah-${refNumber}.pdf`);
    } finally {
      setPreviewing(false);
    }
  }

  const canConfirm = pv != null && slip != null && !paidMutation.isPending;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px] data-[state=open]:animate-in data-[state=open]:fade-in data-[state=closed]:animate-out data-[state=closed]:fade-out" />
        <Dialog.Content className="card-paper fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 space-y-4 overflow-y-auto p-5 focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95">
          <div className="space-y-1">
            <Dialog.Title className="font-serif text-lg text-foreground">{t.payments.markPaidTitle}</Dialog.Title>
            <Dialog.Description className="text-sm text-muted-foreground">{t.payments.markPaidIntro}</Dialog.Description>
          </div>

          <SingleSlot
            label={t.payments.pvLabel}
            doc={pv}
            uploading={uploadMutation.isPending}
            onPick={pickSingle('PV')}
            onRemove={() => pv && deleteMutation.mutate(pv.id)}
          />
          <SingleSlot
            label={t.payments.slipLabel}
            doc={slip}
            uploading={uploadMutation.isPending}
            onPick={pickSingle('SLIP')}
            onRemove={() => slip && deleteMutation.mutate(slip.id)}
          />

          {/* Other docs — multiple */}
          <div className="rounded-lg border border-border p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="status-label text-muted-foreground">{t.payments.otherLabel}</span>
              <label className="btn-secondary btn-sm cursor-pointer gap-1">
                <Icon name="upload" size={14} />
                {t.payments.docUploadBtn}
                <input type="file" multiple accept={UPLOAD_ACCEPT} className="hidden" onChange={(e) => void pickOthers(e)} disabled={uploadMutation.isPending} />
              </label>
            </div>
            {others.length > 0 && (
              <ul className="mt-2 divide-y divide-border text-sm">
                {others.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-2 py-1">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <Icon name="description" size={14} className="shrink-0 text-muted-foreground" />
                      <span className="truncate">{d.fileName}</span>
                    </span>
                    <button type="button" onClick={() => deleteMutation.mutate(d.id)} disabled={deleteMutation.isPending} className="btn-ghost btn-sm text-destructive" aria-label={t.payments.docRemove}>
                      <Icon name="close" size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {err && <p className="text-xs text-destructive">{err}</p>}
          {!canConfirm && <p className="text-xs text-muted-foreground">{t.payments.docsRequiredHint}</p>}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <button type="button" onClick={() => void handlePreview()} disabled={previewing} className="btn-secondary btn-sm gap-1">
              <Icon name="visibility" size={14} />
              {previewing ? t.payments.previewLoading : t.payments.previewPackage}
            </button>
            <div className="flex items-center gap-2">
              <button type="button" className="btn-ghost btn-sm" onClick={() => onOpenChange(false)}>
                {t.payments.cancel}
              </button>
              <button type="button" className="btn-primary btn-sm" disabled={!canConfirm} onClick={() => paidMutation.mutate()}>
                {paidMutation.isPending ? t.payments.updating : t.payments.confirmMarkPaid}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function SingleSlot({
  label,
  doc,
  uploading,
  onPick,
  onRemove,
}: {
  label: string;
  doc: PaymentDoc | null;
  uploading: boolean;
  onPick: (e: ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="status-label text-muted-foreground">{label}</span>
        <label className="btn-secondary btn-sm cursor-pointer gap-1">
          <Icon name="upload" size={14} />
          {uploading ? t.payments.docUploading : doc ? t.payments.docReplaceBtn : t.payments.docUploadBtn}
          <input type="file" accept={UPLOAD_ACCEPT} className="hidden" onChange={onPick} disabled={uploading} />
        </label>
      </div>
      {doc ? (
        <div className="mt-2 flex items-center justify-between gap-2 text-sm">
          <span className="flex min-w-0 items-center gap-1.5">
            <Icon name="description" size={14} className="shrink-0 text-muted-foreground" />
            <span className="truncate">{doc.fileName}</span>
          </span>
          <button type="button" onClick={onRemove} className="btn-ghost btn-sm text-destructive" aria-label={t.payments.docRemove}>
            <Icon name="close" size={14} />
          </button>
        </div>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">{t.payments.docNone}</p>
      )}
    </div>
  );
}
